import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Bar } from 'react-chartjs-2'
import 'chartjs-plugin-datalabels'
import { injectIntl } from 'react-intl'
import WidgetContainer from 'components/Apps/WidgetContainer'
import messages from 'components/Apps/SlaMonitor/translate'
import { getChartsRatio } from 'helpers/slaMonitor'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { resetCageAVEData } from 'reducers/slaMonitor/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import { getFormattedNum } from 'helpers/math'
import { GREY } from 'constants/colors'

class CageActualVsExpected extends Component {
  componentWillUnmount() {
    this.props.resetCageAVEData()
  }

  // eslint-disable-next-line max-statements
  getData = () => {
    const {
      intl: { formatMessage },
    } = this.props
    const values = this.props.cageAvEData.get('data')
    const expected = this.props.cageAvEData.get('feed_expected')
    const labels = []
    const colors = []
    const dataset = []
    const lineDataset = []
    const subtotalDataset = []
    const yTicks = {}
    let prevValue = 0
    let prevPercent = 0

    if (values.length) {
      labels.push('startFake')
      colors.push('rgba(84, 110, 122, .7)')
      dataset.push(null)
      lineDataset.push(100)
      subtotalDataset.push(null)

      for (const key in values) {
        if (Object.prototype.hasOwnProperty.call(values, key)) {
          const item = values[key]
          const value = item.y
          const percent = Math.round((value / expected) * 100 * 10) / 10
          labels.push(`${item.x}`)
          colors.push('rgba(84, 110, 122, .7)')
          dataset.push([prevPercent, prevPercent + percent, value, percent])
          lineDataset.push(100)
          subtotalDataset.push(null)
          prevPercent += percent
          prevValue += value
        }
      }

      labels.push(formatMessage(messages.total))
      colors.push('rgba(84, 110, 122, 1)')
      dataset.push(null)
      lineDataset.push(100)
      subtotalDataset.push([
        0,
        prevPercent,
        prevValue,
        Math.round((prevValue / expected) * 100 * 10) / 10,
      ])
      // eslint-disable-next-line prefer-destructuring
      yTicks.min = labels[1]
      yTicks.max = labels[labels.length - 1]
      labels.push('finishFake')
      dataset.push(null)
      lineDataset.push(100)
      subtotalDataset.push(null)
    }

    return { labels, dataset, lineDataset, subtotalDataset, colors, yTicks }
  }

  chartData = ({ labels, dataset, lineDataset, subtotalDataset, colors }) => {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      labels,
      datasets: [
        {
          label: formatMessage(messages.actualExpected),
          datalabels: {
            color: 'black',
          },
          backgroundColor: colors,
          data: dataset,
        },
        {
          label: formatMessage(messages.actualExpected),
          datalabels: {
            color: 'black',
          },
          backgroundColor: colors,
          data: subtotalDataset,
        },
        {
          type: 'line',
          label: '',
          fill: false,
          pointRadius: 0,
          pointHitRadius: 0,
          pointBorderWidth: 0,
          borderDash: [10, 5],
          borderColor: GREY,
          offset: false,
          data: lineDataset,
        },
      ],
    }
  }

  chartOptions = ({ yTicks }) => {
    const {
      intl: { formatMessage, formatNumber },
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.hour),
            },
            ticks: yTicks,
          },
        ],
        yAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.feed)} (%)`,
            },
          },
        ],
      },
      tooltips: {
        enabled: true,
        mode: 'single',
        intersect: false,
        backgroundColor: '#03254a',
        animationDuration: 300,
        titleFontSize: 13,
        bodyFontSize: 10,
        caretSize: 10,
        displayColors: false,
        callbacks: {
          title: e => `${e[0].label}`,
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex]
            const item = dataset.data[tooltipItem.index]
            const value = Array.isArray(item) ? item[2] : item
            const percent = Array.isArray(item) ? item[3] : item

            return [
              `${dataset.label}: ${formatNumber(value)}`,
              `${formatMessage(messages.total)} %: ${percent}`,
              `${formatMessage(messages.totalFeedUsed)}: ${formatNumber(
                value,
              )} kg`,
            ]
          },
        },
      },
      plugins: {
        datalabels: {
          display: true,
          align: 'center',
          font: {
            weight: 'bold',
            size: ifWindowWidthLessThan(1280) ? 9 : 11,
          },
          formatter: value => {
            if (!Array.isArray(value)) return ''

            const countKg = value[2]
            const percent = value[3]

            return !countKg
              ? `${percent}%`
              : `${percent}% \n(${formatNumber(countKg)})`
          },
        },
      },
    }
  }

  getSideContent = () => {
    const {
      intl: { formatMessage },
      siteLevel,
      goBack,
      cageAvEData,
    } = this.props

    if (ifWindowWidthLessThan(992) && siteLevel !== true) {
      return (
        <button type="button" className="btn-link" onClick={goBack}>
          {formatMessage(messages.back)}
        </button>
      )
    }

    return cageAvEData.get('last_updated')
  }

  render() {
    const {
      cageAvEData,
      chartLoader,
      intl: { formatMessage },
    } = this.props
    const cageData = cageAvEData.toJS()
    const options = this.getData()

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.actualExpected),
          end: `${formatMessage(messages.cage)} ${cageData.locus_name}`,
        }}
        sideTitle={this.getSideContent()}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(Math.round(cageAvEData.get('feed_used')))}
              </span>
              <span className="item-name">{`${formatMessage(
                messages.actual,
              )} (kg)`}</span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(Math.round(cageAvEData.get('feed_expected')))}
              </span>
              <span className="item-name">{`${formatMessage(
                messages.expected,
              )} (kg)`}</span>
            </div>
            <div className="info-item">
              <span className="item-count">{`${Math.round(
                cageAvEData.get('difference') * 100,
              )}%`}</span>
              <span className="item-name">
                {formatMessage(messages.actualExpected)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <Bar
              width={null}
              height={null}
              data={this.chartData(options)}
              options={this.chartOptions(options)}
              datasetKeyProvider={() => Math.random()}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    cageAvEData: state.getIn(['slaMonitor', 'cageAvEData']),
    chartLoader: state.getIn(['slaMonitor', 'cageAVELoader']),
  }),
  {
    resetCageAVEData,
  },
)(injectIntl(CageActualVsExpected))
